import { Alert, Badge, Box, Button, Card, Grid, Group, Skeleton, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { DatePickerInput, MonthPickerInput } from "@mantine/dates";
import { IconAlertTriangle, IconBuildingBank } from "@tabler/icons-react";
import { useState } from "react";
import useSWR from "swr";
import { BillingAccountApi } from "../../apis";
import type { TenantBillingAccount } from "../../apis/billingAccount";
import { ActivityFeed } from "../../components/billing/ActivityFeed";
import { AlertsPanel } from "../../components/billing/dashboard/AlertsPanel";
import { InvoiceStatusCard } from "../../components/billing/dashboard/InvoiceStatusCard";
import { KpiRow } from "../../components/billing/dashboard/KpiRow";
import { PolicyBreakdownDonut } from "../../components/billing/dashboard/PolicyBreakdownDonut";
import { RevenueTrendChart } from "../../components/billing/dashboard/RevenueTrendChart";
import { TopCustomersCard } from "../../components/billing/dashboard/TopCustomersCard";
import { useDashboard } from "../../components/billing/dashboard/useDashboard";
import { PageLayout } from "../../components/layout";

const MONTH_STORAGE_KEY = "dehub.my-billing.month";
const RANGE_STORAGE_KEY = "dehub.my-billing.range";
const MAX_MONTHS = 3;

// "YYYY-MM-DD" for `d` in local time (no UTC shift).
const fmtDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseDate = (s: string): Date => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

const revenueModelLabel: Record<string, string> = {
  PLATFORM_SUBSCRIPTION: "Платформ захиалга",
  TENANT_FEE_BASED: "Шимтгэл суурьтай",
  HYBRID: "Холимог"
};
const accountStatusLabel: Record<string, string> = {
  ACTIVE: "Идэвхтэй", SUSPENDED: "Түр зогссон", CLOSED: "Хаагдсан", PENDING: "Хүлээгдэж буй"
};
const accountStatusColor: Record<string, string> = {
  ACTIVE: "green", SUSPENDED: "orange", CLOSED: "gray", PENDING: "yellow"
};

const accountWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };

function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>{label}</Text>
      {children}
    </Stack>
  );
}

const MyBillingDashboard = () => {
  // Two independent filters (AND-combined server-side), each persisted across reloads:
  //   month     — single month ("YYYY-MM-01" for the picker)
  //   dateRange — day-level range, capped at 3 months
  const [month, setMonth] = useState<string | null>(() =>
    (typeof window !== "undefined" ? window.localStorage.getItem(MONTH_STORAGE_KEY) : null) || null
  );
  const [dateRange, setDateRange] = useState<[string | null, string | null]>(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(RANGE_STORAGE_KEY) : null;
    const [s, e] = saved ? saved.split(",") : [];
    return s && e ? [s, e] : [null, null];
  });

  const onMonthChange = (value: string | null) => {
    setMonth(value);
    if (value) window.localStorage.setItem(MONTH_STORAGE_KEY, value);
    else window.localStorage.removeItem(MONTH_STORAGE_KEY);
  };

  // Clamp the day range to a 3-month span, then persist (or clear) both ends.
  const onDateRangeChange = (value: [string | null, string | null]) => {
    let [s, e] = value;
    if (s && e) {
      const start = parseDate(s);
      const maxEnd = new Date(start.getFullYear(), start.getMonth() + MAX_MONTHS, start.getDate() - 1);
      if (parseDate(e) > maxEnd) e = fmtDate(maxEnd);
    }
    setDateRange([s, e]);
    if (s && e) window.localStorage.setItem(RANGE_STORAGE_KEY, `${s},${e}`);
    else window.localStorage.removeItem(RANGE_STORAGE_KEY);
  };

  const maxDate = fmtDate(new Date());
  const filters = {
    month: month ? month.slice(0, 7) : undefined,
    startDate: dateRange[0] ?? undefined,
    endDate: dateRange[1] ?? undefined,
  };

  // The account ("Данс") card keeps its own fetch and markup — unchanged.
  const { data: accRaw, error: accErr, isLoading: accLoading } =
    useSWR("swr.my.billing-account", () => BillingAccountApi.getMy());
  const account = accRaw as TenantBillingAccount | undefined;

  const { data: dash, error: dashErr, isLoading: dashLoading, mutate } = useDashboard(filters);
  const currency = dash?.account.currency ?? account?.currency ?? "MNT";

  // Adapt the aggregate's {type,title,at} into the shared ActivityFeed's ActivityItem.
  const activityItems = (dash?.activity ?? []).map((a, i) => ({
    id: `${a.type}-${a.at}-${i}`, occurredAt: a.at, type: a.type, summary: a.title
  }));

  const extra = [
    <Group key="filters" gap="sm" align="flex-end" wrap="wrap">
      <MonthPickerInput
        label="Сараар"
        value={month}
        onChange={onMonthChange}
        valueFormat="YYYY-MM"
        maxDate={maxDate}
        clearable
        w={150}
        size="sm"
        placeholder="Сар сонгох"
      />
      <DatePickerInput
        type="range"
        label={
          <Group justify="space-between" gap="xs" wrap="nowrap" w="100%">
            <Text span inherit>Тодорхой хугацаа</Text>
            <Text span size="xs" fw={400} c="dimmed">Хамгийн их 3 сар</Text>
          </Group>
        }
        styles={{ label: { display: "block", width: "100%" } }}
        value={dateRange}
        onChange={onDateRangeChange}
        valueFormat="YYYY-MM-DD"
        maxDate={maxDate}
        clearable
        w={250}
        size="sm"
        placeholder="Эхлэх – дуусах"
      />
    </Group>
  ];

  return (
    <PageLayout title="Тоон тойм" subTitle="Таны billing-ийн тойм" extra={extra}>
      <Stack gap="lg">
        {/* Account header (unchanged) */}
        {accErr ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>Дансны мэдээлэл ачааллахад алдаа гарлаа.</Alert>
        ) : accLoading ? (
          <Skeleton height={92} radius="md" />
        ) : (
          <Card withBorder radius="md" padding="lg" style={accountWash}>
            <Group justify="space-between" wrap="wrap" gap="lg">
              <Group gap="md" wrap="nowrap">
                <ThemeIcon variant="white" color="brand" size={46} radius="md">
                  <IconBuildingBank size={24} />
                </ThemeIcon>
                <Box>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.06em" }}>
                    Billing данс
                  </Text>
                  <Title order={4}>{account?.accountNumber ?? "Таны данс"}</Title>
                  {account?.billingEmail && <Text size="sm" c="dimmed">{account.billingEmail}</Text>}
                </Box>
              </Group>
              <Group gap="xl" wrap="wrap">
                <MetaField label="Орлогын загвар">
                  <Badge color="brand" variant="light" radius="sm">
                    {account ? (revenueModelLabel[account.revenueModel] ?? account.revenueModel) : "—"}
                  </Badge>
                </MetaField>
                <MetaField label="Төлөв">
                  <Badge color={account ? (accountStatusColor[account.status] ?? "gray") : "gray"} variant="light" radius="sm">
                    {account ? (accountStatusLabel[account.status] ?? account.status) : "—"}
                  </Badge>
                </MetaField>
                <MetaField label="Валют">
                  <Text fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>{account?.currency ?? "MNT"}</Text>
                </MetaField>
              </Group>
            </Group>
          </Card>
        )}

        {/* Dashboard widgets — one aggregate fetch; each widget owns its loading/empty state */}
        {dashErr ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Дашбоард ачааллахад алдаа гарлаа">
            <Stack gap="xs" align="flex-start">
              <Text size="sm">Дахин оролдоно уу.</Text>
              <Button size="xs" variant="light" color="red" onClick={() => mutate()}>Дахин ачаалах</Button>
            </Stack>
          </Alert>
        ) : (
          <>
            <KpiRow data={dash} loading={dashLoading} currency={currency} />

            <Grid gap="lg">
              <Grid.Col span={{ base: 12, lg: 8 }}>
                <RevenueTrendChart data={dash} loading={dashLoading} currency={currency} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 4 }}>
                <PolicyBreakdownDonut data={dash} loading={dashLoading} />
              </Grid.Col>
            </Grid>

            <Grid gap="lg">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <InvoiceStatusCard data={dash} loading={dashLoading} currency={currency} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TopCustomersCard data={dash} loading={dashLoading} currency={currency} />
              </Grid.Col>
            </Grid>

            <Grid gap="lg">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <AlertsPanel data={dash} loading={dashLoading} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <ActivityFeed
                  items={activityItems}
                  loading={dashLoading}
                  error={dashErr}
                  onRetry={() => mutate()}
                  title="Идэвхжил"
                  emptyText="Сүүлийн үед идэвхжил алга байна."
                />
              </Grid.Col>
            </Grid>
          </>
        )}
      </Stack>
    </PageLayout>
  );
};

export default MyBillingDashboard;
